import React, { useEffect, useState } from 'react';
import { Clock, MapPin } from 'lucide-react';
import type { EventDraft } from '../types/events';
import { dateMarker, formatLongDate, formatTimes } from '../utils/eventDates';
import { WhatsAppButton } from './WhatsAppButton';
interface EventCardProps {
  event: EventDraft;
}

/**
 * The featured public event card. Also rendered inside /admin as the live
 * preview, so what Zev sees is exactly what visitors get.
 */
export function EventCard({
  event
}: EventCardProps) {
  const [posterFailed, setPosterFailed] = useState(false);
  useEffect(() => {
    setPosterFailed(false);
  }, [event.posterUrl]);
  const showPoster = Boolean(event.posterUrl) && !posterFailed;
  const marker = dateMarker(event.eventDate);
  const longDate = formatLongDate(event.eventDate);
  const times = formatTimes(event.doorsTime, event.showTime);
  return <article className={`grid grid-cols-1 overflow-hidden rounded-sm border border-pine/15 bg-cream ${showPoster ? 'md:grid-cols-12' : ''}`} data-id="element-230" data-mp="1ezbonp6wswdw">
      {showPoster && event.posterUrl && <div className="md:col-span-5 lg:col-span-4" data-id="element-231" data-mp="3cn9x3aw3hajf">
          <img src={event.posterUrl} alt={event.posterAlt ?? `Poster for ${event.title}`} onError={() => setPosterFailed(true)} className="h-full w-full object-cover" data-id="element-232" data-mp="3clp3polinxn5" />
        </div>}

      <div className={`flex flex-col p-7 md:p-10 lg:p-12 ${showPoster ? 'md:col-span-7 lg:col-span-8' : ''}`} data-id="element-233" data-mp="2opb24rbahc3f">
        <div className="mb-8 flex items-start gap-6" data-id="element-234" data-mp="2tp74km4c8vgv">
          {marker && <div className="flex-none border-r border-pine/15 pr-6 text-center" data-id="element-235" data-mp="2xppv8cuhvvhh">
              <span className="block text-xs font-medium uppercase tracking-[0.18em] text-clay/70" data-id="element-236" data-mp="32plq9ml7sckw">
                {marker.weekday}
              </span>
              <span className="block font-serif text-4xl leading-none text-ember" data-id="element-237" data-mp="czihswjnpjdp">
                {marker.day}
              </span>
              <span className="block text-xs font-medium uppercase tracking-[0.18em] text-clay/70" data-id="element-238" data-mp="2e1pzurm023c6">
                {marker.month}
              </span>
            </div>}
          <div className="min-w-0" data-id="element-239" data-mp="2a2qy1fvkemoy">
            {event.tagline && <p className="mb-2 font-serif text-sm italic text-clay/80" data-id="element-240" data-mp="2nznnbcw1bw3q">{event.tagline}</p>}
            <h3 className="break-words font-serif text-3xl leading-tight text-pine md:text-4xl" data-id="element-241" data-mp="2j1m8531qcgxj">
              {event.title}
            </h3>
            {event.subtitle && <p className="mt-2 text-xs font-medium uppercase tracking-[0.22em] text-clay/70" data-id="element-242" data-mp="1qdgiq2zqe3rd">
                {event.subtitle}
              </p>}
          </div>
        </div>

        {event.performers && <div className="mb-8" data-id="element-243" data-mp="209a4mczcb9lt">
            <p className="mb-2 text-xs font-medium uppercase tracking-[0.2em] text-clay/60" data-id="element-244" data-mp="1cl0lxjxfb1yw">
              Performed by
            </p>
            <p className="text-lg font-light leading-relaxed text-clay" data-id="element-245" data-mp="17kw67w4cab9q">{event.performers}</p>
          </div>}

        {(longDate || times || event.address) && <dl className="mb-10 grid grid-cols-1 gap-4 border-t border-pine/15 pt-6 sm:grid-cols-2" data-id="element-246" data-mp="3nzpskv18gvjz">
            {(longDate || times) && <div className="flex items-start gap-3" data-id="element-247" data-mp="3a9vzg0ip3p4g">
                <Clock className="mt-0.5 h-4 w-4 flex-none text-ember" data-id="element-248" data-mp="3e9wqckmzdsse" />
                <div data-id="element-249" data-mp="3e83g6vd5sqfn">
                  <dt className="sr-only" data-id="element-250" data-mp="5umievwettcp">Date and times</dt>
                  <dd className="font-light text-clay" data-id="element-251" data-mp="5v5z69abqbp5">
                    {longDate}
                    {longDate && times && <br data-id="element-252" data-mp="ard727ma5x9q" />}
                    {times}
                  </dd>
                </div>
              </div>}
            {event.address && <div className="flex items-start gap-3" data-id="element-253" data-mp="1rgehuwrse9e">
                <MapPin className="mt-0.5 h-4 w-4 flex-none text-ember" data-id="element-254" data-mp="2hjx981srlly7" />
                <div data-id="element-255" data-mp="2hm07f942mle0">
                  <dt className="sr-only" data-id="element-256" data-mp="2mj0ffpkhdy7r">Location</dt>
                  <dd className="font-light text-clay" data-id="element-257" data-mp="2mkladgapjj65">
                    Pines Retreat
                    <br data-id="element-258" data-mp="31tf64jsde8ig" />
                    {event.address}
                  </dd>
                </div>
              </div>}
          </dl>}

        <div className="mt-auto flex flex-col gap-3 sm:flex-row sm:items-center" data-id="element-259" data-mp="2s2kibazh70uw">
          <WhatsAppButton label="Reserve a spot" data-id="element-260" data-mp="2o18rj24v89l4" />
          <p className="text-sm font-light leading-relaxed text-clay/70" data-id="element-261" data-mp="2o2ju4ymkrban">
            Message Zev to hold your place — the dome seats a limited number.
          </p>
        </div>
      </div>
    </article>;
}